import { getAnalytics } from "@tm/insurance/analytics";
import { navigationActions } from "@tm/insurance/navigation";
import { TextNodeType } from "@tm/ui-widgets/components";
import { theme } from "@tm/utility/global";
import FlexComp from "../FlexComp/FlexComp";
import FormattedText from "../FormattedText/FormattedText";
import { ImageComponent } from "../ImageComponent/ImageComponent";
import AnchorTag from "../TextStyles/AnchorTag";
import {
  CancellationSection,
  PlanHeader,
  PlanPriceContainer,
  Separator,
} from "./PlanPriceInfoComp.styles";

type PlanInfo = {
  name: {
    text: {
      value: string;
      styles?: any;
    };
  };
  icon: string;
};

type CancellationInfo = {
  title: TextNodeType;
  info: {
    prefix: TextNodeType;
    date: number;
  };
};

type PlanPriceInfoData = {
  planType: TextNodeType;
  planInfo: PlanInfo;
  sumInsured: TextNodeType;
  amountToPay: TextNodeType;
  cancellationInfo: CancellationInfo;
};

const formatDate = (timestamp: number | undefined): string => {
  if (!timestamp) return "";
  const date = new Date(timestamp * 1000);
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, "0");

  return `${day} ${month}, ${year}, ${formattedHours}:${formattedMinutes}${ampm} IST`;
};

const PlanPriceInfoComp = (data: PlanPriceInfoData) => {
  const analytics = getAnalytics();
  const { planType, planInfo, sumInsured, amountToPay, cancellationInfo } =
    data;

  const handleEditClick = () => {
    analytics?.trackTravDetailsEvents({
      actionType: "edit_plan_price_info_clicked",
    });
    if (document.referrer) {
      navigationActions.goBack();
    }
    const queryParams = new URLSearchParams(window.location.search);
    const params = {
      countries: queryParams.get("countries"),
      startDate: queryParams.get("startDate"),
      endDate: queryParams.get("endDate"),
      travellers: queryParams.get("travellers"),
      segment: queryParams.get("segment"),
      type: queryParams.get("type") || "",
      itineraryId: queryParams.get("itineraryId") || "",
    };
    navigationActions.navigateToListing(params);
  };

  const formattedDate = formatDate(cancellationInfo?.info?.date);

  return (
    <PlanPriceContainer>
      <PlanHeader>
        <FlexComp justifyContent="space-between" alignItems="center">
          <FormattedText
            value={planType}
            baseStyle={{
              fontWeight: 900,
              fontSize: "14px",
            }}
          />
          <AnchorTag handleClick={handleEditClick}>
            <ImageComponent
              width="24px"
              height="24px"
              src="https://tripmoneycmsimgak.mmtcdn.com/img/edit_Icon_be6c24806a.svg"
            />
          </AnchorTag>
        </FlexComp>
      </PlanHeader>

      <Separator />

      <FlexComp
        justifyContent="space-between"
        alignItems="flex-start"
        margin="0 0 12px 0"
      >
        <FlexComp gap="12px" alignItems="center">
          {planInfo.icon && (
            <ImageComponent width="22px" height="22px" src={planInfo.icon} />
          )}
          <FormattedText
            value={planInfo.name}
            baseStyle={{
              fontWeight: 900,
              fontSize: "16px",
              color: theme.blackText,
            }}
          />
        </FlexComp>

        <FlexComp flexDirection="column" alignItems="flex-end" gap="4px">
          <FormattedText
            value={sumInsured}
            baseStyle={{
              fontWeight: 400,
              fontSize: "16px",
              color: theme.greyText,
            }}
          />
          <FormattedText
            value={amountToPay}
            baseStyle={{
              fontWeight: 400,
              fontSize: "16px",
              color: theme.greyText,
            }}
          />
        </FlexComp>
      </FlexComp>
      {cancellationInfo && (
        <CancellationSection>
          <FormattedText
            value={cancellationInfo.title}
            baseStyle={{
              fontWeight: 900,
              fontSize: "16px",
              color: theme.blackText,
            }}
          />
          <FlexComp gap="4px">
            <FormattedText
              value={cancellationInfo.info?.prefix}
              baseStyle={{
                fontSize: "16px",
                color: theme.blackText,
              }}
            />
            <FormattedText
              value={`${formattedDate}`}
              baseStyle={{
                fontSize: "16px",
                color: theme.blackText,
                fontWeight: 700,
              }}
            />
          </FlexComp>
        </CancellationSection>
      )}
    </PlanPriceContainer>
  );
};

export default PlanPriceInfoComp;
